import type { Tables } from "@/types/database";

type Hotspot = Tables<"hotspots">;
type Project = Tables<"projects">;

export type TextVariableContext = {
  hotspot: Hotspot;
  project: Project | null;
  targetPanoramaName: string;
  currentPanoramaName: string;
};

// ── CMS row cache (populated by useTextVariableFieldSync) ──

/** Hotspot ID → CMS row data */
let _hotspotCMSRows: Map<string, Record<string, unknown>> = new Map();
/** Panorama ID → CMS row data */
let _panoramaCMSRows: Map<string, Record<string, unknown>> = new Map();

export const setCMSRowCache = (
  hotspotRows: Map<string, Record<string, unknown>>,
  panoramaRows: Map<string, Record<string, unknown>>,
) => {
  _hotspotCMSRows = hotspotRows;
  _panoramaCMSRows = panoramaRows;
};

const VARIABLE_RE = /\[\s*(hotspots|panoramas)\s*\.\s*([a-z0-9_]+)\s*\]/gi;

/**
 * Resolve text variables of the form `[table.field]`.
 *
 * The input may be HTML (from TipTap rich-text editor).
 *
 * Supported tables:
 *  - hotspots  → looks up the field on the current hotspot / CMS row
 *  - panoramas → looks up the field on the current panorama CMS row
 */
export const resolveTextVariables = (value: string, context: TextVariableContext): string => {
  if (!value) return value;

  const normalized = normalizeVariableMarkup(value);

  if (!normalized.includes("[")) return normalized;

  if (!normalized.includes("<")) {
    return replaceVariableTokens(normalized, context);
  }

  if (typeof document === "undefined") {
    return replaceVariableTokens(normalized, context);
  }

  const template = document.createElement("template");
  template.innerHTML = normalized;

  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
  let textNode = walker.nextNode();
  while (textNode) {
    const current = textNode as Text;
    current.nodeValue = replaceVariableTokens(current.nodeValue ?? "", context);
    textNode = walker.nextNode();
  }

  // TipTap can split a token across inline nodes. If the normal text-node pass
  // still leaves a token behind, collapse the affected block's text content and
  // resolve it as one string so `[hotspots.name] x` keeps rendering.
  if (template.innerHTML.includes("[")) {
    for (const block of template.content.querySelectorAll("p, li, blockquote")) {
      const textContent = block.textContent ?? "";
      if (!textContent.includes("[")) continue;
      const resolvedText = replaceVariableTokens(textContent, context);
      if (resolvedText !== textContent) {
        block.textContent = resolvedText;
      }
    }
  }

  return template.innerHTML;
};

function normalizeVariableMarkup(value: string): string {
  return value
    .replace(/[\u200B\u200C\u200D\uFEFF]/g, "")
    .replace(/\u00A0/g, " ")
    .replace(/&#91;|&#x5b;|&lbrack;/gi, "[")
    .replace(/&#93;|&#x5d;|&rbrack;/gi, "]");
}

function replaceVariableTokens(value: string, context: TextVariableContext): string {
  return value.replace(VARIABLE_RE, (_match, table: string, field: string) => {
    const tableName = table.toLowerCase();
    const fieldName = field.toLowerCase();
    if (tableName === "hotspots") return resolveHotspotField(context, fieldName);
    if (tableName === "panoramas") return resolvePanoramaField(context, fieldName);
    return _match;
  });
}

function resolveHotspotField(context: TextVariableContext, field: string): string {
  const { hotspot } = context;

  // Direct top-level fields
  if (field === "name") return hotspot.name || "";

  // Fields derived from content blob (matching CMS row generation)
  if (field === "hotspot_style") {
    const content =
      typeof hotspot.content === "object" && hotspot.content !== null
        ? (hotspot.content as Record<string, unknown>)
        : {};
    if (Array.isArray(content.stylePresetIds)) {
      return content.stylePresetIds.filter((value): value is string => typeof value === "string").join(",");
    }
    return typeof content.stylePresetId === "string" ? content.stylePresetId : "";
  }

  // Custom fields – look up from cached CMS row data
  const hotspotId = hotspot.id ?? "";
  const cmsRow =
    _hotspotCMSRows.get(hotspotId) ??
    Array.from(_hotspotCMSRows.values()).find((row) => {
      const rowName = row.name;
      return typeof rowName === "string" && rowName === hotspot.name;
    });
  if (cmsRow) {
    const val = lookupRowField(cmsRow, field);
    if (val != null) return String(val);
  }

  return "";
}

function resolvePanoramaField(context: TextVariableContext, field: string): string {
  if (field === "name") return context.currentPanoramaName || "";

  // Custom fields – look up from cached CMS panorama row data
  // Try to find the panorama row by name since we don't have the ID in context
  for (const row of _panoramaCMSRows.values()) {
    if (row.name === context.currentPanoramaName) {
      const val = lookupRowField(row, field);
      if (val != null) return String(val);
      break;
    }
  }

  return "";
}

function lookupRowField(
  row: Record<string, unknown>,
  field: string,
): unknown {
  if (field in row) return row[field];

  const normalizedTarget = normalizeFieldKey(field);
  for (const [key, value] of Object.entries(row)) {
    if (normalizeFieldKey(key) === normalizedTarget) {
      return value;
    }
  }

  const content =
    row.content && typeof row.content === "object" ? (row.content as Record<string, unknown>) : null;
  if (content) {
    if (field in content) return content[field];
    for (const [key, value] of Object.entries(content)) {
      if (normalizeFieldKey(key) === normalizedTarget) {
        return value;
      }
    }
  }

  return undefined;
}

function normalizeFieldKey(value: string): string {
  return value.trim().replace(/[\s-]+/g, "_").toLowerCase();
}
